import { useState, useEffect, useMemo } from "react";
import { set, z } from "zod";
import { createRideApi } from "services/auth/rideApis";
import MapAutoComplete from "@/components/MapAutoComplete.jsx";
import { useNavigate } from "react-router-dom";
import { Label } from "../components/ui/label.js";
import { Input } from "../components/ui/input.js";
import { Button } from "../components/ui/button.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select.js";
import toast from "react-hot-toast";
import {
  getInterestedUsersApi,
  getTopRequestedRoutesApi,
} from "services/auth/interestApis.js";
import { FaArrowRight } from "react-icons/fa";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip.js";
import { FiMapPin, FiUsers, FiCalendar, FiCreditCard, FiNavigation, FiFileText } from "react-icons/fi";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion.js";

// Zod schema

const createRideSchema = z
  .object({
    from: z.string().min(1, "Source location is required"),
    to: z.string().min(1, "Destination is required"),
    capacity: z
      .number()
      .int()
      .min(1, "Capacity must be at least 1")
      .max(20, "Capacity must not exceed 20"),
    date: z.string().refine((val) => new Date(val) > new Date(), {
      message: "Ride datetime must be 30 min in the future",
    }),
    totalFare: z
      .number()
      .int()
      .min(0, "Per head cost must be at least 0")
      .max(5000, "Per head cost must not exceed 5000"),
    vehicleType: z.enum(["CAR", "BIKE", "VAN", "AUTO"]),
    description: z.string().optional(),
    recurrence: z
      .object({
        isRecurring: z.boolean(),
        recurrenceEndDate: z.string().optional(),
        selectedDays: z.array(z.number()).optional(),
      })
      .optional(),
  })
  .refine((data) => data.from.toLowerCase() !== data.to.toLowerCase(), {
    message: "Source and destination cannot be the same",
    path: ["to"],
  })
  .refine(
    (data) => {
      if (data.recurrence?.isRecurring) {
        return (
          data.recurrence.recurrenceEndDate &&
          data.recurrence.selectedDays &&
          data.recurrence.selectedDays.length > 0 &&
          new Date(data.recurrence.recurrenceEndDate) > new Date(data.date)
        );
      }
      return true;
    },
    {
      message:
        "End date must be after start date and at least one day must be selected",
      path: ["recurrence"],
    }
  );

type CreateRideForm = z.infer<typeof createRideSchema>;

interface RouteSuggestion {
  from: string;
  to: string;
  count: number;
}

export const CreateRide = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rideForm, setRideForm] = useState<
    Omit<CreateRideForm, "capacity" | "totalFare"> & {
      capacity: string;
      totalFare: string;
    }
  >({
    from: "",
    to: "",
    capacity: "",
    date: "",
    totalFare: "",
    vehicleType: "CAR",
    description: "",
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateRideForm, string>>
  >({});
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [topSuggestions, setTopSuggestions] = useState<RouteSuggestion[]>([]);
  const [checkInterestedUsers, setCheckInterestedUsers] = useState<string>("");

  useEffect(() => {
    async function fetchTopInterests() {
      try {
        const response = await getTopRequestedRoutesApi();
        setTopSuggestions(response || []);
      } catch (error) {
        console.error("Error fetching top interests:", error);
      }
    }

    fetchTopInterests();
  }, []);

  useEffect(() => {
    setCheckInterestedUsers("");
    async function fetchInterestedUsers() {
      const response = await getInterestedUsersApi(rideForm.from, rideForm.to);
      if (response > 1)
        setCheckInterestedUsers(
          `${response} users are interested in this route.`,
        );
    }

    if (rideForm.from.length > 0 && rideForm.to.length > 0) {
      fetchInterestedUsers();
    }
  }, [rideForm.from, rideForm.to]);

  // Handler for GoogleMapsAutocomplete
  const setFrom = (val: string) => {
    setRideForm({ ...rideForm, from: val });
    setErrors({ ...errors, from: undefined });
  };

  const setTo = (val: string) => {
    if (val.toLowerCase() === rideForm.from.toLowerCase()) {
      setErrors({ to: "Source and destination cannot be the same" });
      toast.error("Source and destination cannot be the same");
      return;
    }
    setRideForm({ ...rideForm, to: val });
    setErrors({ ...errors, to: undefined });
  };

  const daysOfWeek = [
    { label: "Sun", value: 0 },
    { label: "Mon", value: 1 },
    { label: "Tue", value: 2 },
    { label: "Wed", value: 3 },
    { label: "Thu", value: 4 },
    { label: "Fri", value: 5 },
    { label: "Sat", value: 6 },
  ];

  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // Preview count for recurring rides
  const previewCount = useMemo(() => {
    if (!isRecurring || !rideForm.date || !recurrenceEndDate || selectedDays.length === 0) return 0;
    const start = new Date(rideForm.date);
    const end = new Date(recurrenceEndDate);
    if (end <= start) return 0;
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      if (selectedDays.includes(current.getDay())) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  }, [isRecurring, rideForm.date, recurrenceEndDate, selectedDays]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (
      rideForm.from.toLowerCase() === rideForm.to.toLowerCase() &&
      rideForm.from !== ""
    ) {
      setErrors({ to: "Source and destination cannot be the same" });
      toast.error("Source and destination cannot be the same");
      return;
    }

    try {
      const recurrenceData = isRecurring
        ? { isRecurring: true, recurrenceEndDate, selectedDays }
        : undefined;

      // Convert string values to numbers for validation
      const formData = {
        ...rideForm,
        capacity: rideForm.capacity ? parseInt(rideForm.capacity, 10) : 0,
        totalFare: rideForm.totalFare ? parseInt(rideForm.totalFare, 10) : 0,
        recurrence: recurrenceData,
      };

      // Validate form data
      createRideSchema.parse(formData);

      setLoading(true);
      const data = await createRideApi(
        formData.from,
        formData.to,
        formData.capacity,
        formData.date,
        formData.totalFare,
        formData.vehicleType,
        formData.description || "",
        recurrenceData
      );

      if (data) {
        navigate("/");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof CreateRideForm, string>> = {};
        err.issues.forEach((issue) => {
          const field = issue.path[0] as keyof CreateRideForm;
          fieldErrors[field] = issue.message;
        });
        setErrors(fieldErrors);

        const firstIssue = err.issues[0];
        if (firstIssue) {
          toast.error(firstIssue.message);
        }
      } else {
        const errorMessage =
          (err as any)?.response?.data?.message ||
          (err as any)?.message ||
          "An error occurred. Please try again.";
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30); // Add 0.5 hour (30 min) padding
    const pad = (n: number) => n.toString().padStart(2, "0");
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChooseRouteSuggsestion = (route: {
    from: string;
    to: string;
  }) => {
    setRideForm({ ...rideForm, from: route.from, to: route.to });
  };

  function sliceAddress(address: string, maxLength = 40) {
    if (!address || address.length <= maxLength) return address;

    const sliced = address.slice(0, maxLength);
    const lastSpace = sliced.lastIndexOf(" ");

    return sliced.slice(0, lastSpace) + "...";
  }

  return (
    <div className="flex justify-center items-center min-h-screen py-4 px-2 backdrop-blur max-sm:backdrop-blur-sm">
      <div className="overflow-hidden w-full max-w-md h-fit py-4">
        <form
          onSubmit={handleSubmit}
          className="bg-white mx-auto p-4 sm:p-6 md:p-8 rounded-lg shadow-md w-full space-y-4 flex flex-col justify-center"
          style={{ minHeight: "fit-content" }}
        >
          <h2 className="text-2xl font-semibold mb-4 text-center">
            Add a Ride
          </h2>
          <div className="flex justify-center mb-2">
            <h3 className="text-sm text-orange-600 bg-orange-50 border border-orange-100 px-3 py-1.5 rounded-md font-medium text-center">
              <strong>Note : </strong>All fields are mandatory !
            </h3>
          </div>

          {topSuggestions.length > 0 && (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="suggestions" className="w-full font-medium">
                <AccordionTrigger className="font-medium cursor-pointer hover:no-underline ">
                  Top Requested Routes:
                </AccordionTrigger>
                <AccordionContent className="text-balance flex flex-wrap gap-2 mt-1">
                  {topSuggestions.map((route, index) => (
                    <Tooltip key={index}>
                      {/* Added max-w-full to Trigger/Button ensures it doesn't overflow the screen width */}
                      <TooltipTrigger
                        className="font-normal max-w-full"
                        asChild
                      >
                        <Button
                          type="button"
                          className="flex flex-row gap-1 capitalize justify-center items-center bg-orange-50 text-orange-900 text-sm px-3 py-1 rounded-full cursor-pointer hover:bg-orange-100 max-w-full"
                          onClick={() => handleChooseRouteSuggsestion(route)}
                        >
                          {/* Route From: truncate + flex-1 splits space equally */}
                          <span className="truncate min-w-0 flex-1 text-right">
                            {route.from}
                          </span>

                          <FaArrowRight className="shrink-0 text-xs" />

                          {/* Route To: truncate + flex-1 splits space equally */}
                          <span className="truncate min-w-0 flex-1 text-left">
                            {route.to}
                          </span>
                        </Button>
                      </TooltipTrigger>

                      <TooltipContent>
                        {/* Good UX: Show the full address here since it is truncated in the button */}
                        <p className="font-semibold text-xs mb-1 border-b pb-1">
                          {route.from} <span className="mx-1">→</span>{" "}
                          {route.to}
                        </p>
                        <p> Interested Users: {route.count} </p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          <div>
            <Label htmlFor="from" className="flex items-center gap-2 mb-1"><FiMapPin className="w-4 h-4 text-zinc-500" /> From *</Label>
            <MapAutoComplete
              value={rideForm.from}
              placeholder={"Enter source location"}
              changeHandler={setFrom}
            />
            {errors.from && (
              <p className="text-red-500 text-sm mt-1">{errors.from}</p>
            )}
          </div>

          <div>
            <Label htmlFor="to" className="flex items-center gap-2 mb-1"><FiMapPin className="w-4 h-4 text-zinc-500" /> To *</Label>
            <MapAutoComplete
              value={rideForm.to}
              placeholder={"Enter destination"}
              changeHandler={setTo}
            />
            {errors.to && (
              <p className="text-red-500 text-sm mt-1">{errors.to}</p>
            )}
            {checkInterestedUsers.length > 0 && (
              <p className="text-sm text-green-700 font-normal">
                {checkInterestedUsers}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="capacity" className="flex items-center gap-2 mb-1"><FiUsers className="w-4 h-4 text-zinc-500" /> Capacity *</Label>
            <Input
              type="number"
              id="capacity"
              name="capacity"
              value={rideForm.capacity}
              onChange={(e) => {
                setRideForm({ ...rideForm, capacity: e.target.value });
                setErrors({ ...errors, capacity: undefined });
              }}
              min="1"
              max="20"
              placeholder="Enter capacity"
              className={errors.capacity ? "border-red-500" : ""}
            />
            {errors.capacity && (
              <p className="text-red-500 text-sm mt-1">{errors.capacity}</p>
            )}
          </div>

          <div>
            <Label htmlFor="date" className="flex items-center gap-2 mb-1"><FiCalendar className="w-4 h-4 text-zinc-500" /> Date & Time *</Label>
            <Input
              type="datetime-local"
              id="date"
              name="date"
              value={rideForm.date}
              onChange={(e) => {
                setRideForm({ ...rideForm, date: e.target.value });
                setErrors({ ...errors, date: undefined });
              }}
              min={getMinDateTime()}
              className={errors.date ? "border-red-500" : ""}
            />
            {errors.date && (
              <p className="text-red-500 text-sm mt-1">{errors.date}</p>
            )}
          </div>

          {/* Recurring Ride Section */}
          <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <Label htmlFor="recurring" className="cursor-pointer font-medium flex items-center gap-2">
                <FiCalendar className="w-4 h-4 text-zinc-500" /> Make this a recurring ride
              </Label>
              <button
                type="button"
                id="recurring"
                role="switch"
                aria-checked={isRecurring}
                onClick={() => setIsRecurring(!isRecurring)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  isRecurring ? "bg-orange-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isRecurring ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {isRecurring && (
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <Label htmlFor="recurrenceEndDate" className="flex items-center gap-2 mb-1"><FiCalendar className="w-4 h-4 text-zinc-500" /> End Date *</Label>
                  <Input
                    type="date"
                    id="recurrenceEndDate"
                    name="recurrenceEndDate"
                    value={recurrenceEndDate}
                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                    min={rideForm.date ? rideForm.date.split("T")[0] : ""}
                    className={errors.recurrence ? "border-red-500" : ""}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-1"><FiCalendar className="w-4 h-4 text-zinc-500" /> Repeat on *</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {daysOfWeek.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors cursor-pointer ${
                          selectedDays.includes(day.value)
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-white text-gray-700 border-gray-300 hover:border-orange-300"
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {previewCount > 0 && (
                  <p className="text-sm text-orange-700 font-medium bg-orange-50 px-3 py-2 rounded">
                    This will create {previewCount} rides between{" "}
                    {rideForm.date ? rideForm.date.split("T")[0] : "start"} and{" "}
                    {recurrenceEndDate || "end"}
                  </p>
                )}

                {(errors as any).recurrence && (
                  <p className="text-red-500 text-sm">
                    {(errors as any).recurrence}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="totalFare" className="flex items-center gap-2 mb-1"><FiCreditCard className="w-4 h-4 text-zinc-500" /> Per head cost *</Label>
            <Input
              type="number"
              id="totalFare"
              name="totalFare"
              value={rideForm.totalFare}
              onChange={(e) => {
                setRideForm({ ...rideForm, totalFare: e.target.value });
                setErrors({ ...errors, totalFare: undefined });
              }}
              min="0"
              max="5000"
              placeholder="Enter per head cost"
              className={errors.totalFare ? "border-red-500" : ""}
            />
            {errors.totalFare && (
              <p className="text-red-500 text-sm mt-1">{errors.totalFare}</p>
            )}
          </div>

          <div>
            <Label htmlFor="vehicleType" className="flex items-center gap-2 mb-1"><FiNavigation className="w-4 h-4 text-zinc-500" /> Vehicle Type *</Label>
            <Select
              value={rideForm.vehicleType}
              onValueChange={(value) => {
                setRideForm({
                  ...rideForm,
                  vehicleType: value as "CAR" | "BIKE" | "VAN" | "AUTO",
                });
                setErrors({ ...errors, vehicleType: undefined });
              }}
            >
              <SelectTrigger
                className={errors.vehicleType ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select vehicle type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CAR">Car</SelectItem>
                <SelectItem value="BIKE">Bike</SelectItem>
                <SelectItem value="VAN">Van</SelectItem>
                <SelectItem value="AUTO">Auto</SelectItem>
              </SelectContent>
            </Select>
            {errors.vehicleType && (
              <p className="text-red-500 text-sm mt-1">{errors.vehicleType}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description" className="flex items-center gap-2 mb-1"><FiFileText className="w-4 h-4 text-zinc-500" /> Description</Label>
            <textarea
              id="description"
              name="description"
              value={rideForm.description}
              onChange={(e) => {
                setRideForm({ ...rideForm, description: e.target.value });
                setErrors({ ...errors, description: undefined });
              }}
              placeholder="Enter description (optional)"
              className={`w-full h-20 resize-none border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                errors.description ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className={loading ? "opacity-60 cursor-not-allowed" : ""}
          >
            {loading ? "Creating Ride..." : "Create Ride"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreateRide;
